import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsInputAntennaOutlinedIcon from "@mui/icons-material/SettingsInputAntennaOutlined";
import TroubleshootOutlinedIcon from "@mui/icons-material/TroubleshootOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import GlassCard from "../../components/dashboard/GlassCard";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";
import { useTranslation } from "react-i18next";
import { usePlatform } from "../../context/PlatformContext";
import { platformMockService } from "../../services/mock/platformMockService";
import { getMonitoringCameras, updateLegacyMonitoringCamera } from "../../services/real/monitoringCameras";
import { mergeMonitoringCameras } from "../../services/adapters/monitoringCameras";
import CameraPager, { cameraPage } from "../../components/monitoring/CameraPager";
import CameraHls from "../../components/monitoring/CameraHls";
import { addVideoToken, useVideoToken } from "../../utils/videoToken";
import ResizableCameraPaper from "../../components/monitoring/ResizableCameraPaper";

const emptyCamera = {
  name: "",
  display_name: "",
  rtsp_url: "",
  branch_id: "",
  zone_name: "",
};

const statusColor = (status) => {
  if (status === "online") return "success";
  if (status === "maintenance") return "warning";
  if (status === "error") return "error";
  return "default";
};

const sourceLabel = (source, t) => ({
  manual: t("platform.cameras.sourceManual"),
  agent: t("platform.cameras.sourceAgent"),
  legacy: "Legacy",
  published: t("platform.monitoringTools.publishedSource"),
}[source] || source || t("platform.cameras.sourceManualFallback"));

const formatDate = (value, t, locale) => {
  if (!value) return t("platform.common.noConnection");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(locale || "es-AR");
};

const isHeartbeatStale = (camera) => {
  if (camera.source !== "agent" || !camera.last_heartbeat) return false;
  return Date.now() - new Date(camera.last_heartbeat).getTime() > 2 * 60 * 1000;
};

const getUserEmail = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user.email) return user.email;
    const token = localStorage.getItem("token");
    if (!token) return "";
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64 + "=".repeat((4 - b64.length % 4) % 4))).email || "";
  } catch (_) {
    return "";
  }
};

const apiBase = () => (process.env.REACT_APP_API_BASE || "").replace(/\/v1\/api\/?$/, "").replace(/\/$/, "");

const originalStreamUrl = (camera) => camera.hls_url || `/mjpeg_feed/${encodeURIComponent(camera.name)}`;

// Sin token es la URL para mostrar; con token, la que se le pide al proxy.
const aiStreamUrl = (camera, videoToken = null) => {
  if (camera.ai_stream_url) return camera.ai_stream_url;
  const email = getUserEmail();
  if (!email) return "";
  return addVideoToken(`${apiBase()}/api/proxy/ai-stream/${encodeURIComponent(email)}/${encodeURIComponent(String(camera.name || "").replace(/ /g, "_"))}`, videoToken);
};

const StreamPane = ({ title, src, mode, camera }) => {
  const { t } = useTranslation();
  const [key, setKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setKey((current) => current + 1);
    setLoading(true);
    setError(false);
  }, [src]);

  return (
    <Box sx={{ minWidth: 0 }} data-stream-pane={mode}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography fontWeight={600}>{title}</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => { setKey((current) => current + 1); setLoading(true); setError(false); }}>{t("platform.cameras.reconnect")}</Button>
      </Stack>
      <Box sx={{ position: "relative", bgcolor: "#020617", borderRadius: "8px", overflow: "hidden", aspectRatio: "16 / 9", border: "1px solid", borderColor: "divider" }}>
        {loading && !/\.m3u8(?:\?|$)/.test(src || "") && <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2 }} />}
        {src && !error && /\.m3u8(?:\?|$)/.test(src) ? <CameraHls key={`hls-${key}`} url={src} /> : src && !error ? (
          <Box
            key={`${mode}-${key}`}
            component="img"
            src={src}
            alt={`${camera.name}-${mode}`}
            onLoad={() => setLoading(false)}
            onError={() => { setLoading(false); setError(true); }}
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Stack alignItems="center" justifyContent="center" height="100%" spacing={1}>
            <ErrorOutlineIcon color="warning" />
            <Typography color="text.secondary">{src ? t("platform.cameras.streamDown") : t("platform.cameras.streamNotConfigured")}</Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const UnifiedCameras = () => {
  const { t, i18n } = useTranslation();
  const { showMockData } = usePlatform();
  const videoToken = useVideoToken(apiBase(), getUserEmail());
  const [cameraPageIndex, setCameraPageIndex] = useState(0);
  const dataVersionRef = useRef(0);
  const [branches, setBranches] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerMode, setViewerMode] = useState("both");
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [form, setForm] = useState(emptyCamera);
  const [editForm, setEditForm] = useState({});
  const [lastTest, setLastTest] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    setSelectedBranch("");
    setSourceFilter("");
    setStatusFilter("");
    setSearch("");
    setOpen(false);
    setEditOpen(false);
    setDiagOpen(false);
    setViewerOpen(false);
    setSelectedCamera(null);
  }, [showMockData]);

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);

  const fetchData = useCallback(async () => {
    const version = ++dataVersionRef.current;
    setLoading(true);
    try {
      const mock = platformMockService.getDashboard();
      const [branchRows, cameraPayload] = await Promise.all([
        showMockData ? Promise.resolve([]) : saasApi.getBranches(),
        showMockData ? Promise.resolve({ cameras: mergeMonitoringCameras({ cameras: mock.liveCameras, streams: mock.aiStreams }).map(camera => ({ ...camera, managementSource: "mock" })) }) : getMonitoringCameras({ branchId: selectedBranch }),
      ]);
      if (version !== dataVersionRef.current) return;
      setBranches(branchRows);
      setCameras(cameraPayload.cameras);
    } catch (err) {
      if (version === dataVersionRef.current) notify(err.message, "error");
    } finally {
      if (version === dataVersionRef.current) setLoading(false);
    }
  }, [notify, selectedBranch, showMockData]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => { clearInterval(id); dataVersionRef.current += 1; };
  }, [fetchData]);

  const branchOptions = useMemo(() => branches.map((branch) => ({
    value: branch.id,
    label: branch.name,
  })), [branches]);

  const filteredCameras = useMemo(() => {
    return cameras.filter((camera) => {
      const text = `${camera.name} ${camera.display_name || ""} ${camera.zone_name || ""} ${camera.ip || ""} ${camera.device_id || ""}`.toLowerCase();
      if (sourceFilter && camera.source !== sourceFilter) return false;
      if (statusFilter && camera.status !== statusFilter) return false;
      if (search && !text.includes(search.toLowerCase())) return false;
      return true;
    });
  }, [cameras, search, sourceFilter, statusFilter]);
  const visibleCameras = cameraPage(filteredCameras, cameraPageIndex);
  useEffect(() => { setCameraPageIndex(0); }, [selectedBranch, sourceFilter, statusFilter, search, showMockData]);

  const agentCameras = useMemo(() => cameras.filter((camera) => camera.source === "agent"), [cameras]);
  const agentSummary = useMemo(() => {
    const devices = new Set(agentCameras.map((camera) => camera.device_id).filter(Boolean));
    const online = agentCameras.filter((camera) => camera.status === "online" && !isHeartbeatStale(camera)).length;
    const latest = agentCameras.map((camera) => camera.last_heartbeat).filter(Boolean).sort().pop();
    const errors = agentCameras.filter((camera) => camera.last_error || camera.status === "error");
    return { total: agentCameras.length, online, devices: devices.size, latest, errors };
  }, [agentCameras]);

  const saveCamera = async () => {
    if (showMockData) return;
    try {
      await saasApi.createCamera({
        ...form,
        display_name: form.display_name || form.name,
        branch_id: form.branch_id || branches[0]?.id,
      });
      // Sin esto la camara no aparece en "Video AI Procesado", que lee la tabla legacy.
      try {
        await saasApi.createLegacyCamera({ name: form.name, rtsp_url: form.rtsp_url });
      } catch (mirrorErr) {
        notify(t("platform.cameras.createdAiMirrorWarning", { message: mirrorErr.message }), "warning");
      }
      setOpen(false);
      setForm(emptyCamera);
      notify(t("platform.cameras.created"));
      fetchData();
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const openEdit = (camera) => {
    setSelectedCamera(camera);
    setEditForm({
      display_name: camera.display_name || camera.name,
      branch_id: camera.branch_id || "",
      zone_name: camera.zone_name || "",
      rtsp_url: ["manual", "legacy"].includes(camera.source) ? (camera.rtsp_url || "") : "",
      status: camera.status || "offline",
      is_active: camera.is_active !== 0,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (showMockData || !["saas", "legacy"].includes(selectedCamera?.managementSource)) return;
    try {
      if (selectedCamera.managementSource === "legacy") {
        await updateLegacyMonitoringCamera(selectedCamera, editForm.rtsp_url);
      } else await saasApi.updateCamera(selectedCamera.id, {
        ...editForm,
        rtsp_url: selectedCamera.source === "manual" ? editForm.rtsp_url : undefined,
      });
      notify(t("platform.cameras.updated"));
      setEditOpen(false);
      fetchData();
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const archiveCamera = async (camera) => {
    if (showMockData || !["saas", "legacy"].includes(camera.managementSource)) return;
    if (!window.confirm(t("platform.cameras.archiveConfirm"))) return;
    try {
      if (camera.source !== "legacy") await saasApi.archiveCamera(camera.id);
      if (camera.legacy_id) await saasApi.archiveLegacyCamera(camera.legacy_id);
      notify(t("platform.cameras.archived"));
      fetchData();
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const testCamera = async (camera) => {
    if (showMockData || !["saas", "legacy"].includes(camera.managementSource)) return;
    setLastTest(null);
    try {
      const result = await saasApi.testCameraConnection(camera.managementSource === "legacy" ? { rtsp_url: camera.rtsp_url } : { camera_id: camera.id });
      setLastTest({ camera_id: camera.id, ...result });
      notify(`${result.message}. ${result.latency_ms || 0}ms`);
      fetchData();
    } catch (err) {
      setLastTest({ camera_id: camera.id, success: false, message: err.message });
      notify(err.message, "error");
      fetchData();
    }
  };

  const openViewer = (camera, mode = "both") => {
    setSelectedCamera(camera);
    setViewerMode(mode);
    setViewerOpen(true);
  };

  const openDiagnostics = (camera) => {
    setSelectedCamera(camera);
    setDiagOpen(true);
  };

  const heartbeatAlert = agentSummary.total > 0 && agentSummary.online === 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box className="scene-toolbar" display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Header title={t("platform.pages.cameras.title")} subtitle={t("platform.pages.cameras.subtitle")} />
        <Stack direction="row" gap={1} flexWrap="wrap">
          {showMockData && <Chip label={t("platform.topbar.showMockData")} color="secondary" variant="outlined" />}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>{t("platform.cameras.sync")}</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} disabled={showMockData}>{t("platform.cameras.newCamera")}</Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {heartbeatAlert && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("platform.cameras.heartbeatAlert", { date: formatDate(agentSummary.latest, t, i18n.language) })}
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} lg={5}>
          <GlassCard hover={false} sx={{ height: "100%" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
              <Stack direction="row" gap={1.5}>
                <SettingsInputAntennaOutlinedIcon color={agentSummary.online > 0 ? "success" : "warning"} />
                <Box>
                  <Typography variant="h5">{t("platform.cameras.windowsAgent")}</Typography>
                  <Typography color="text.secondary">{t("platform.cameras.lastConnection")}: {formatDate(agentSummary.latest, t, i18n.language)}</Typography>
                </Box>
              </Stack>
              <Chip label={agentSummary.online > 0 ? t("platform.status.online") : t("platform.status.offline")} color={agentSummary.online > 0 ? "success" : "error"} />
            </Stack>
            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              <Grid item xs={6} sm={3}><Typography color="text.secondary">{t("platform.common.cameras")}</Typography><Typography variant="h5">{agentSummary.total}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography color="text.secondary">{t("platform.status.online")}</Typography><Typography variant="h5">{agentSummary.online}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography color="text.secondary">Devices</Typography><Typography variant="h5">{agentSummary.devices}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography color="text.secondary">{t("platform.common.errors")}</Typography><Typography variant="h5">{agentSummary.errors.length}</Typography></Grid>
            </Grid>
            <Stack spacing={0.75} mt={2}>
              {agentCameras.slice(0, 3).map((camera) => (
                <Stack key={camera.id} direction="row" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" noWrap>{camera.device_id || "device"} · {camera.ip || t("platform.cameras.noIp")}</Typography>
                  <Typography variant="body2" color="text.secondary">{camera.agent_version || t("platform.cameras.versionNotReported")}</Typography>
                </Stack>
              ))}
            </Stack>
          </GlassCard>
        </Grid>
        <Grid item xs={12} lg={7}>
          <GlassCard hover={false} sx={{ height: "100%" }}>
            <Typography variant="h5" mb={1}>{t("platform.cameras.installationTitle")}</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}><Chip label={t("platform.cameras.installStep1")} color="primary" /><Typography variant="body2" mt={1}>{t("platform.cameras.installStep1Body")}</Typography></Grid>
              <Grid item xs={12} md={4}><Chip label={t("platform.cameras.installStep2")} color="primary" /><Typography variant="body2" mt={1}>{t("platform.cameras.installStep2Body")}</Typography></Grid>
              <Grid item xs={12} md={4}><Chip label={t("platform.cameras.installStep3")} color="primary" /><Typography variant="body2" mt={1}>{t("platform.cameras.installStep3Body")}</Typography></Grid>
            </Grid>
          </GlassCard>
        </Grid>
      </Grid>

      <GlassCard hover={false} sx={{ mb: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField select size="small" label={t("platform.common.store")} value={selectedBranch} onChange={(event) => setSelectedBranch(event.target.value)} fullWidth>
              <MenuItem value="">{t("platform.common.all")}</MenuItem>
              {branchOptions.map((branch) => <MenuItem key={branch.value} value={branch.value}>{branch.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select size="small" label={t("platform.common.source")} value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)} fullWidth>
              <MenuItem value="">{t("platform.common.all")}</MenuItem>
              <MenuItem value="manual">Manual RTSP</MenuItem>
              <MenuItem value="agent">{t("platform.cameras.sourceAgentShort")}</MenuItem>
              <MenuItem value="legacy">Legacy</MenuItem>
              <MenuItem value="published">{t("platform.monitoringTools.publishedSource")}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select size="small" label={t("platform.common.status")} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} fullWidth>
              <MenuItem value="">{t("platform.common.all")}</MenuItem>
              <MenuItem value="online">{t("platform.status.online")}</MenuItem>
              <MenuItem value="offline">{t("platform.status.offline")}</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="maintenance">{t("platform.status.maintenance")}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField size="small" label={t("platform.cameras.searchPlaceholder")} value={search} onChange={(event) => setSearch(event.target.value)} fullWidth />
          </Grid>
        </Grid>
      </GlassCard>

      <Box display="flex" justifyContent="flex-end" mb={2}><CameraPager count={filteredCameras.length} page={visibleCameras.page} onChange={setCameraPageIndex} /></Box>
      <Grid container spacing={2.5}>
        {visibleCameras.items.map((camera) => {
          const stale = isHeartbeatStale(camera);
          const aiUrl = aiStreamUrl(camera);
          return (
            <Grid item xs={12} md={6} key={camera.id} data-managed-camera={camera.name}>
              <GlassCard hover={false} sx={{ height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
                    <Box sx={{ color: "primary.main" }}><VideocamOutlinedIcon /></Box>
                    <Box minWidth={0}>
                      <Typography variant="h5" noWrap>{camera.display_name || camera.name}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>{camera.branch_name || t("platform.common.noStore")} · {camera.zone_name || t("platform.common.noZone")}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" gap={0.5}>
                    {["saas", "legacy"].includes(camera.managementSource) && (
                      <Tooltip title={t("common.edit")}><IconButton onClick={() => openEdit(camera)}><EditOutlinedIcon /></IconButton></Tooltip>
                    )}
                    {["saas", "legacy"].includes(camera.managementSource) && <Tooltip title={t("platform.cameras.archive")}><IconButton color="error" onClick={() => archiveCamera(camera)}><DeleteOutlineIcon /></IconButton></Tooltip>}
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" rowGap={1}>
                  <Chip label={stale ? t("platform.cameras.heartbeatExpired") : (camera.status || t("platform.status.offline"))} color={stale ? "error" : statusColor(camera.status)} size="small" />
                  <Chip label={sourceLabel(camera.source, t)} variant="outlined" size="small" />
                  {camera.ip && <Chip icon={<LanOutlinedIcon />} label={camera.ip} variant="outlined" size="small" />}
                </Stack>

                <Box sx={{ mt: 2, borderRadius: "8px", overflow: "hidden", bgcolor: "#000", aspectRatio: "16 / 9", position: "relative" }}>
                  {!showMockData && camera.hls_url ? <CameraHls url={camera.hls_url} /> : <Box
                    component="img"
                    src={showMockData ? camera.snapshot_url || camera.ai?.snapshot_url : camera.ai ? addVideoToken(`${apiBase()}/api/proxy/ai-snapshot/${encodeURIComponent(getUserEmail())}/${encodeURIComponent(camera.ai.camera_id.replace(/ /g, "_"))}`, videoToken) : `/snapshot/${encodeURIComponent(camera.name)}`}
                    alt={camera.name}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = "https://placehold.co/640x360/111827/38bdf8?text=Sin+Preview";
                    }}
                  />}
                  <Button size="small" variant="contained" startIcon={<FullscreenOutlinedIcon />} onClick={() => openViewer(camera, "both")} sx={{ position: "absolute", right: 8, bottom: 8 }}>{t("common.view")}</Button>
                </Box>

                <Grid container spacing={1.5} mt={0.5}>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t("platform.cameras.lastConnection")}</Typography><Typography variant="body2" noWrap>{formatDate(camera.last_heartbeat, t, i18n.language)}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Device ID</Typography><Typography variant="body2" noWrap>{camera.device_id || t("platform.common.notApplicable")}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t("platform.cameras.agentVersion")}</Typography><Typography variant="body2" noWrap>{camera.agent_version || t("platform.cameras.notReported")}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t("platform.cameras.aiStream")}</Typography><Typography variant="body2" noWrap>{aiUrl ? t("platform.common.available") : t("platform.common.notConfigured")}</Typography></Grid>
                </Grid>

                {camera.last_error && <Alert severity="error" sx={{ mt: 2 }}>{camera.last_error}</Alert>}

                <Stack direction="row" gap={1} mt={2} flexWrap="wrap">
                  <Button size="small" variant="outlined" startIcon={<PlayCircleOutlineIcon />} onClick={() => testCamera(camera)} disabled={!(["saas", "legacy"].includes(camera.managementSource)) || (camera.managementSource === "legacy" && !camera.rtsp_url)}>{t("platform.cameras.realTest")}</Button>
                  <Button size="small" variant="outlined" startIcon={<TroubleshootOutlinedIcon />} onClick={() => openDiagnostics(camera)}>{t("platform.cameras.diagnostics")}</Button>
                  <Button size="small" variant="outlined" startIcon={<CompareArrowsOutlinedIcon />} onClick={() => openViewer(camera, "both")}>{t("platform.cameras.originalVsAi")}</Button>
                </Stack>
              </GlassCard>
            </Grid>
          );
        })}
        {!loading && filteredCameras.length === 0 && (
          <Grid item xs={12}>
            <GlassCard hover={false}>
              <Typography color="text.secondary">{t("platform.cameras.empty")}</Typography>
            </GlassCard>
          </Grid>
        )}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("platform.cameras.newManualTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label={t("platform.cameras.uniqueId")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField label={t("platform.cameras.displayName")} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} fullWidth />
            <TextField select label={t("platform.common.store")} value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} fullWidth>
              {branches.map((branch) => <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>)}
            </TextField>
            <TextField label={t("platform.cameras.sectorZone")} value={form.zone_name} onChange={(e) => setForm({ ...form, zone_name: e.target.value })} fullWidth />
            <TextField label="RTSP" value={form.rtsp_url} onChange={(e) => setForm({ ...form, rtsp_url: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={saveCamera} disabled={!form.name || !form.rtsp_url}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("platform.cameras.safeEdit")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info">{t("platform.cameras.safeEditHint")}</Alert>
            {selectedCamera?.managementSource !== "legacy" && <>
            <TextField label={t("platform.cameras.displayName")} value={editForm.display_name || ""} onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })} fullWidth />
            <TextField select label={t("platform.common.store")} value={editForm.branch_id || ""} onChange={(e) => setEditForm({ ...editForm, branch_id: e.target.value })} fullWidth>
              {branches.map((branch) => <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>)}
            </TextField>
            <TextField label={t("platform.cameras.sectorZone")} value={editForm.zone_name || ""} onChange={(e) => setEditForm({ ...editForm, zone_name: e.target.value })} fullWidth />
            </>}
            {["manual", "legacy"].includes(selectedCamera?.source) && (
              <TextField label="RTSP" value={editForm.rtsp_url || ""} onChange={(e) => setEditForm({ ...editForm, rtsp_url: e.target.value })} fullWidth />
            )}
            {selectedCamera?.managementSource !== "legacy" && <TextField select label={t("platform.cameras.operationalStatus")} value={editForm.status || "offline"} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} fullWidth>
              <MenuItem value="online">{t("platform.status.online")}</MenuItem>
              <MenuItem value="offline">{t("platform.status.offline")}</MenuItem>
              <MenuItem value="maintenance">{t("platform.status.maintenance")}</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </TextField>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={saveEdit}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={diagOpen} onClose={() => setDiagOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("platform.cameras.diagnosticsTitle")}</DialogTitle>
        <DialogContent>
          {selectedCamera && (
            <Stack spacing={1.5} mt={1}>
              <Typography variant="h5">{selectedCamera.display_name || selectedCamera.name}</Typography>
              <Chip label={sourceLabel(selectedCamera.source, t)} />
              <Typography>{t("platform.common.status")}: {selectedCamera.status}</Typography>
              <Typography>{t("platform.cameras.lastConnection")}: {formatDate(selectedCamera.last_heartbeat, t, i18n.language)}</Typography>
              <Typography>Device ID: {selectedCamera.device_id || t("platform.common.notApplicable")}</Typography>
              <Typography>{t("platform.cameras.localIp")}: {selectedCamera.ip || t("platform.cameras.notReported")}</Typography>
              <Typography>{t("platform.cameras.agentVersion")}: {selectedCamera.agent_version || t("platform.cameras.notReported")}</Typography>
              <Typography>{t("platform.cameras.visibleRtsp")}: {selectedCamera.rtsp_url ? t("platform.status.configured") : t("platform.cameras.notVisible")}</Typography>
              <Typography>Stream original: {originalStreamUrl(selectedCamera)}</Typography>
              <Typography>{t("platform.cameras.aiStream")}: {aiStreamUrl(selectedCamera) || t("platform.common.notConfigured")}</Typography>
              {selectedCamera.last_error && <Alert severity="error">{selectedCamera.last_error}</Alert>}
              {lastTest?.camera_id === selectedCamera.id && (
                <Alert severity={lastTest.success ? "success" : "error"}>{lastTest.message}</Alert>
              )}
              <Button variant="contained" startIcon={<PlayCircleOutlineIcon />} onClick={() => testCamera(selectedCamera)} disabled={!["saas", "legacy"].includes(selectedCamera.managementSource) || (selectedCamera.managementSource === "legacy" && !selectedCamera.rtsp_url)}>{t("platform.cameras.runRealTest")}</Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDiagOpen(false)}>{t("common.close")}</Button></DialogActions>
      </Dialog>

      <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} maxWidth={false} PaperComponent={ResizableCameraPaper}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h5">{selectedCamera?.display_name || selectedCamera?.name}</Typography>
              <Typography variant="body2" color="text.secondary">{t("platform.cameras.viewerSubtitle")}</Typography>
            </Box>
            <IconButton aria-label={t("common.close")} onClick={() => setViewerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedCamera && (
            <Stack spacing={2}>
              <ToggleButtonGroup exclusive value={viewerMode} onChange={(_, value) => value && setViewerMode(value)} size="small">
                <ToggleButton value="original">{t("platform.cameras.original")}</ToggleButton>
                <ToggleButton value="ai">{t("platform.cameras.ai")}</ToggleButton>
                <ToggleButton value="both">{t("platform.cameras.both")}</ToggleButton>
              </ToggleButtonGroup>
              <Grid container spacing={2}>
                {(viewerMode === "original" || viewerMode === "both") && (
                  <Grid item xs={12} md={viewerMode === "both" ? 6 : 12}>
                    <StreamPane title={t("platform.cameras.originalCamera")} src={showMockData ? selectedCamera.snapshot_url || selectedCamera.ai?.snapshot_url : originalStreamUrl(selectedCamera)} mode="original" camera={selectedCamera} />
                  </Grid>
                )}
                {(viewerMode === "ai" || viewerMode === "both") && (
                  <Grid item xs={12} md={viewerMode === "both" ? 6 : 12}>
                    <StreamPane title={t("platform.cameras.aiProcessed")} src={showMockData ? selectedCamera.ai?.snapshot_url || selectedCamera.snapshot_url : aiStreamUrl(selectedCamera, videoToken)} mode="ai" camera={selectedCamera} />
                  </Grid>
                )}
              </Grid>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default UnifiedCameras;
